import { PlatformType } from '@/types/authorization'
import { resolveEnvironmentName } from '@/lib/environment'
import { constructWorkflowMaxAuthUrl } from '@/lib/brwfm'
export { toDatabaseName } from '@/lib/naming'
import type { PricingOption, SignupCustomerForm } from '../types'

export function sanitizePhoneInput(value: string): string {
  return value.replace(/[^0-9()+ -]/g, '').slice(0, 20)
}

const EMPTY_PLATFORM_GUID = '00000000-0000-0000-0000-000000000000'

export function normalizeSignupPlatformId(id: string | undefined): string {
  return id && id !== EMPTY_PLATFORM_GUID ? id : ''
}

export function constructSignupAuthUrl(
  product: PlatformType,
  currentPlatformId: string,
  clientId: string,
  userId: string
): string {
  const envName = resolveEnvironmentName()

  if (product === PlatformType.BRWFM) {
    return constructWorkflowMaxAuthUrl()
  }

  if (!clientId || !userId) return ''
  let baseUrl = ''
  if (envName === 'production' || envName === 'prod') {
    baseUrl = 'https://xero-bulk-sso-prod.dashboardinsights.com/'
  } else if (envName === 'portal') {
    baseUrl = 'https://xero-bulk-sso-portal.dashboardinsights.com/'
  } else if (envName === 'staging') {
    baseUrl = 'https://xero-bulk-sso-staging.dashboardinsights.com/'
  } else if (envName === 'devtest' || envName === 'dev' || envName === 'test') {
    baseUrl = 'https://xero-bulk-sso-dev.dashboardinsights.com/'
  } else {
    baseUrl = 'https://xero-bulk-sso-dev.dashboardinsights.com/'
  }

  const query = new URLSearchParams({ clientId, userId })
  if (currentPlatformId && currentPlatformId !== EMPTY_PLATFORM_GUID && envName !== 'production' && envName !== 'prod' && envName !== 'portal') {
    query.set('platformId', currentPlatformId)
  }
  return `${baseUrl}?${query.toString()}`
}

export function normalizeCustomerForm(
  values: Partial<SignupCustomerForm>,
  knownEmailExists = false
): SignupCustomerForm {
  const safe = (input?: string) => (typeof input === 'string' ? input : '')
  const normalizedEmail = safe(values.email).trim().toLowerCase()
  const normalizedCompany = safe(values.companyName).trim()
  const normalizedShort = safe(values.companyshortName).trim()
  const normalizedFirst = safe(values.firstName).trim()
  const normalizedLast = safe(values.lastName).trim()
  const normalizedPhone = safe(values.phone).trim()
  const normalizedCode = safe(values.code) || 'AU'
  const next: SignupCustomerForm = {
    ...values,
    firstName: normalizedFirst,
    lastName: normalizedLast,
    email: normalizedEmail,
    companyName: normalizedCompany,
    companyshortName: normalizedShort,
    phone: normalizedPhone,
    code: normalizedCode,
    password: safe(values.password),
    isaccepted: Boolean(values.isaccepted),
    address: normalizedCode,
  }

  if (normalizedCode !== 'CA') {
    next.state = undefined
  }
  if (normalizedCode !== 'US') {
    next.postalCode = undefined
  }

  return { ...next, isEmailExists: knownEmailExists }
}

export function getProductLabel(product?: PlatformType, isBasSignup?: boolean, isAdvicePrepSignup?: boolean): string {
  if (isAdvicePrepSignup) return 'AdvicePrep'
  if (isBasSignup) return 'BAS Xero'
  if (product === PlatformType.Xero) return 'Xero'
  if (product === PlatformType.Karbon) return 'Karbon'
  if (product === PlatformType.FYI) return 'FYI'
  if (product === PlatformType.BRWFM) return 'WFM'
  return 'Platform'
}

export const platformOptions = [
  { label: 'Xero', value: PlatformType.Xero },
  { label: 'Karbon', value: PlatformType.Karbon },
  { label: 'FYI', value: PlatformType.FYI },
  { label: 'WorkflowMax', value: PlatformType.BRWFM },
]

export const countryOptions = [
  { value: 'AU', label: 'AU +61' },
  { value: 'CA', label: 'CA +1' },
  { value: 'GB', label: 'GB +44' },
  { value: 'NZ', label: 'NZ +64' },
  { value: 'US', label: 'US +1' },
  { value: 'ZA', label: 'ZA +27' },
]

export const caProvinceOptions = [
  'Alberta',
  'British Columbia',
  'Manitoba',
  'Newfoundland and Labrador',
  'Nova Scotia',
  'Prince Edward Island',
  'Ontario',
  'Quebec',
  'New Brunswick',
  'Saskatchewan',
].map((value) => ({ value, label: value }))

export const defaultPricingOptions: PricingOption[] = [
  { value: 'plan1', label: '1 to 10', price: '25' },
  { value: 'plan2', label: '11 to 25', price: '20' },
  { value: 'plan3', label: '26 to 75', price: '15' },
  { value: 'plan4', label: '75+', price: 'Contact' },
]
